import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { config } from 'src/app/config/config';
import { EventTime } from 'src/app/modules/event-calendar/domain/recurrence/event.time';
import {
  createRecurrenceRule,
  FrequencyKind,
  RecurrenceRule,
  withRule,
} from 'src/app/modules/event-calendar/domain/recurrence/recurrence.rule';
import { EventTimeService } from 'src/app/modules/event-calendar/services/event.time.service';
import { OccurrencePreviewFormatter } from 'src/app/modules/event-calendar/services/occurrence.preview.formatter';
import { OccurrenceService } from 'src/app/modules/event-calendar/services/occurrence.service';
import { DateTimePickerComponent } from '../date.time.picker/date.time.picker.component';
import { SelectFieldComponent } from '../select.field/select.field.component';

interface FrequencyDropdownItem {
  text: string;
  value: FrequencyKind;
  selected?: boolean;
}

@Component({
  selector: 'ig-event-frequency-picker',
  templateUrl: './event.frequency.picker.component.html',
  styleUrls: ['./event.frequency.picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EventFrequencyPickerComponent),
      multi: true,
    },
  ],
})
export class EventFrequencyPickerComponent implements OnInit, ControlValueAccessor {
  @Input() timezoneFormatter: string;
  @Input() timezone: string;

  @ViewChild('select') select?: SelectFieldComponent;
  @ViewChild('mainDateTimePicker') mainDateTimePicker?: DateTimePickerComponent;

  readonly GROUP_SIZE = 25;

  private readonly fb = inject(FormBuilder);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly occurrenceService = inject(OccurrenceService);
  private readonly formatter = inject(OccurrencePreviewFormatter);
  private readonly timeService = inject(EventTimeService);

  rangeForm: FormGroup = this.fb.group({
    startTime: [null as string | null, [Validators.required]],
    endTime: [null as string | null],
    isAllDay: [false],
    isEndAllDay: [false],
  });

  frequencyItems: FrequencyDropdownItem[] = [];
  occurrences: EventTime[] = [];
  formattedOccurrences: string[] = [];
  groupedOccurrences: { label: string; times: EventTime[]; offset: number }[] = [];
  totalLabel = '';
  message = '';
  tzNameByFormatter = '';

  customizeMode = false;
  editingIndex: number | null = null;
  editingFormGroup: FormGroup | null = null;
  isAddingNewEntry = false;
  activeGroupIndex: number | null = null;

  private _rule: RecurrenceRule | null = null;
  private _onChange: (value: RecurrenceRule | null) => void = () => {};
  private _onTouched: () => void = () => {};
  private _disabled = false;

  trackByIndex = (index: number) => index;

  ngOnInit(): void {
    this.initFrequencyItems();
    this.tzNameByFormatter =
      this.timeService.getTZNameByFormatter(this.timezoneFormatter) || '';
    this.rangeForm.valueChanges.subscribe(() => this.onRangeFormChange());
  }

  writeValue(value: RecurrenceRule | null): void {
    this._rule = value ?? null;
    if (value) {
      this.rangeForm.patchValue(
        {
          startTime: value.range.startDateTime,
          endTime: value.range.endDateTime,
          isAllDay: value.isAllDay,
          isEndAllDay: value.isEndAllDay,
        },
        { emitEvent: false },
      );
      this.refreshFrequencyLabels();
      this.regenerateOccurrences();
      this.setSelectedFrequency(value.kind);
    } else {
      this.mainDateTimePicker?.clear();
      this.rangeForm.reset(
        {
          startTime: null,
          endTime: null,
          isAllDay: false,
          isEndAllDay: false,
        },
        { emitEvent: false },
      );
      this.occurrences = [];
      this.formattedOccurrences = [];
      this.groupedOccurrences = [];
      this.totalLabel = '';
      this.message = '';
      this.customizeMode = false;
      this.editingIndex = null;
      this.editingFormGroup = null;
      this.isAddingNewEntry = false;
      this.activeGroupIndex = null;
      this.refreshFrequencyLabels();
      this.setSelectedFrequency('custom');
    }
    this.cdRef.markForCheck();
  }

  reset(): void {
    this.writeValue(null);
  }

  registerOnChange(fn: (value: RecurrenceRule | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this._disabled = disabled;
    if (disabled) this.rangeForm.disable({ emitEvent: false });
    else this.rangeForm.enable({ emitEvent: false });
    this.cdRef.markForCheck();
  }

  onFrequencySelected(item: { value: FrequencyKind } | FrequencyKind): void {
    const kind = (typeof item === 'string' ? item : item?.value) as FrequencyKind;
    if (!kind) return;
    this.message = '';

    const range = this.currentRange();
    if (this.requiresRange(kind)) {
      const missing = this.findMissingRangeFields(range);
      if (missing) {
        this.message = `⚠️ Please set ${missing} for the event`;
        return;
      }
    }

    const switchingToCustomFromNonCustom = kind === 'custom' && this._rule?.kind !== 'custom';
    const nextRule = createRecurrenceRule({
      kind,
      range: { startDateTime: range.startTime, endDateTime: range.endTime ?? range.startTime },
      timezone: this.resolvedTimezone(),
      isAllDay: !!range.isAllDay,
      isEndAllDay: !!range.isEndAllDay,
      explicit: kind === 'custom'
        ? (switchingToCustomFromNonCustom
            ? [{ startTime: range.startTime!, endTime: range.endTime ?? null, isAllDay: !!range.isAllDay, isEndAllDay: !!range.isEndAllDay }]
            : this.occurrences)
        : undefined,
    });

    this.commitRule(nextRule);
  }

  onRangeFormChange(): void {
    if (!this._rule) {
      this.tryBootstrapRule();
      return;
    }
    const range = this.currentRange();
    if (!range.startTime) return;
    try {
      // Re-bootstrap when the single auto-created occurrence is still in sync with the range:
      // lets the date/time picker update the displayed occurrence instead of freezing it at the initial pick.
      const isBootstrappedSingle =
        this._rule.kind === 'custom' &&
        this._rule.explicit?.length === 1 &&
        this._rule.explicit[0].startTime === this._rule.range.startDateTime;

      if (isBootstrappedSingle) {
        this.tryBootstrapRule();
        return;
      }

      const nextRule = withRule(this._rule, {
        range: { startDateTime: range.startTime, endDateTime: range.endTime ?? range.startTime },
        isAllDay: !!range.isAllDay,
        isEndAllDay: !!range.isEndAllDay,
      });
      this.commitRule(nextRule);
      this.refreshFrequencyLabels();
    } catch {
      // invalid intermediate state — wait for next valid edit
    }
  }

  enterCustomizeMode(): void {
    this.customizeMode = true;
    this.cdRef.markForCheck();
  }

  exitCustomizeMode(): void {
    this.customizeMode = false;
    this.editingIndex = null;
    this.editingFormGroup = null;
    this.cdRef.markForCheck();
  }

  startEdit(flatIndex: number): void {
    this.isAddingNewEntry = false;
    const time = this.occurrences[flatIndex];
    this.editingIndex = flatIndex;
    this.editingFormGroup = this.fb.group({
      startTime: [time.startTime, [Validators.required]],
      endTime: [time.endTime || null],
      isAllDay: [time.isAllDay || false],
      isEndAllDay: [time.isEndAllDay || false],
    });
    this.cdRef.markForCheck();
  }

  saveEdit(flatIndex: number): void {
    if (!this.editingFormGroup) return;
    const v = this.editingFormGroup.value;

    if (this.isAddingNewEntry) {
      if (!v.startTime) {
        this.editingFormGroup.get('startTime')?.markAsTouched();
        return;
      }
      this.occurrences = [
        ...this.occurrences,
        { startTime: v.startTime, endTime: v.endTime, isAllDay: v.isAllDay, isEndAllDay: v.isEndAllDay },
      ];
      this.isAddingNewEntry = false;
    } else {
      this.occurrences = [
        ...this.occurrences.slice(0, flatIndex),
        { startTime: v.startTime, endTime: v.endTime, isAllDay: v.isAllDay, isEndAllDay: v.isEndAllDay },
        ...this.occurrences.slice(flatIndex + 1),
      ];
    }

    this.editingIndex = null;
    this.editingFormGroup = null;
    this.flipToCustomWithCurrentOccurrences();
    this.cdRef.markForCheck();
  }

  deleteTime(flatIndex: number): void {
    this.occurrences = [
      ...this.occurrences.slice(0, flatIndex),
      ...this.occurrences.slice(flatIndex + 1),
    ];
    this.editingIndex = null;
    this.editingFormGroup = null;
    if (this.activeGroupIndex !== null && this.activeGroupIndex >= this.groupedOccurrences.length) {
      this.activeGroupIndex = this.groupedOccurrences.length > 0 ? this.groupedOccurrences.length - 1 : null;
    }
    this.flipToCustomWithCurrentOccurrences();
    this.cdRef.markForCheck();
  }

  addTime(): void {
    const isFirstEntry = this.occurrences.length === 0;
    const last = this.occurrences[this.occurrences.length - 1];
    const range = this.currentRange();

    const defaultStart = isFirstEntry ? range.startTime : null;
    const defaultEnd = isFirstEntry ? range.endTime : null;
    const defaultIsAllDay = isFirstEntry ? !!range.isAllDay : last?.isAllDay || false;
    const defaultIsEndAllDay = isFirstEntry ? !!range.isEndAllDay : last?.isEndAllDay || false;

    this.isAddingNewEntry = true;
    this.editingIndex = null;
    this.editingFormGroup = this.fb.group({
      startTime: [defaultStart, [Validators.required]],
      endTime: [defaultEnd],
      isAllDay: [defaultIsAllDay],
      isEndAllDay: [defaultIsEndAllDay],
    });
    this.customizeMode = true;
    this.setSelectedFrequency('custom');
    if (this.occurrences.length >= this.GROUP_SIZE) {
      this.activeGroupIndex = this.groupedOccurrences.length > 0 ? this.groupedOccurrences.length - 1 : null;
    }
    this.cdRef.markForCheck();
  }

  cancelEdit(): void {
    this.isAddingNewEntry = false;
    this.editingIndex = null;
    this.editingFormGroup = null;
    this.cdRef.markForCheck();
  }

  toggleGroup(index: number): void {
    this.activeGroupIndex = this.activeGroupIndex === index ? null : index;
    this.customizeMode = false;
    this.editingIndex = null;
    this.editingFormGroup = null;
    this.cdRef.markForCheck();
  }

  setEditingAllDay(value: boolean): void {
    this.editingFormGroup?.get('isAllDay')?.setValue(value);
  }

  setEditingEndAllDay(value: boolean): void {
    this.editingFormGroup?.get('isEndAllDay')?.setValue(value);
  }

  get editingStartControl(): AbstractControl | null {
    return this.editingFormGroup?.get('startTime') ?? null;
  }

  get editingEndControl(): AbstractControl | null {
    return this.editingFormGroup?.get('endTime') ?? null;
  }

  get currentKind(): FrequencyKind {
    return this._rule?.kind ?? 'custom';
  }

  private commitRule(rule: RecurrenceRule): void {
    this._rule = rule;
    this.regenerateOccurrences();
    this.setSelectedFrequency(rule.kind);
    this._onChange(rule);
    this._onTouched();
    this.cdRef.markForCheck();
  }

  private regenerateOccurrences(): void {
    if (!this._rule) {
      this.occurrences = [];
      this.formattedOccurrences = [];
      this.groupedOccurrences = [];
      this.totalLabel = '';
      return;
    }
    this.occurrences = this.occurrenceService.expand(this._rule);
    const tz = this.resolvedTimezoneIana();
    this.formattedOccurrences = this.occurrences.map(o => this.formatter.occurrenceLabel(o, tz));
    this.groupedOccurrences = this.formatter.groupOccurrences(this.occurrences, this.GROUP_SIZE, tz);
    this.totalLabel =
      this._rule.kind === 'custom' || this._rule.kind === 'event'
        ? ''
        : this.formatter.countAndRangeLabel(this.occurrences, this.timezoneCode());
  }

  private flipToCustomWithCurrentOccurrences(): void {
    const range = this.currentRange();
    if (!range.startTime) return;
    const nextRule = createRecurrenceRule({
      kind: 'custom',
      range: { startDateTime: range.startTime, endDateTime: range.endTime ?? range.startTime },
      timezone: this.resolvedTimezone(),
      isAllDay: !!range.isAllDay,
      isEndAllDay: !!range.isEndAllDay,
      explicit: this.occurrences,
    });
    this.commitRule(nextRule);
  }

  private tryBootstrapRule(): void {
    const range = this.currentRange();
    if (!range.startTime) return;
    const rule = createRecurrenceRule({
      kind: 'custom',
      range: { startDateTime: range.startTime, endDateTime: range.endTime ?? range.startTime },
      timezone: this.resolvedTimezone(),
      isAllDay: !!range.isAllDay,
      isEndAllDay: !!range.isEndAllDay,
      explicit: [
        {
          startTime: range.startTime,
          endTime: range.endTime ?? null,
          isAllDay: !!range.isAllDay,
          isEndAllDay: !!range.isEndAllDay,
        },
      ],
    });
    this.commitRule(rule);
    this.refreshFrequencyLabels();
  }

  private setSelectedFrequency(kind: FrequencyKind): void {
    this.frequencyItems = this.frequencyItems.map(x => ({ ...x, selected: x.value === kind }));
    this.select?.setSelected?.(kind as any);
  }

  private currentRange(): {
    startTime: string | null;
    endTime: string | null;
    isAllDay: boolean;
    isEndAllDay: boolean;
  } {
    return {
      startTime: this.rangeForm.get('startTime')?.value ?? null,
      endTime: this.rangeForm.get('endTime')?.value ?? null,
      isAllDay: !!this.rangeForm.get('isAllDay')?.value,
      isEndAllDay: !!this.rangeForm.get('isEndAllDay')?.value,
    };
  }

  private requiresRange(kind: FrequencyKind): boolean {
    return kind !== 'event' && kind !== 'custom';
  }

  private findMissingRangeFields(range: { startTime: string | null; endTime: string | null }): string {
    if (!range.startTime) return 'start date';
    return '';
  }

  private resolvedTimezone(): string {
    return this.timezone || this.timezoneFormatter || 'America/New_York';
  }

  private resolvedTimezoneIana(): string {
    if (this.timezoneFormatter && this.timezoneFormatter.includes('/')) return this.timezoneFormatter;
    return this.timeService.getTimezoneFormatterByTimezone(this.resolvedTimezone());
  }

  private timezoneCode(): string {
    return this.timezone || this.timezoneFormatter || '';
  }

  private initFrequencyItems(): void {
    this.frequencyItems = [
      { text: 'Custom', value: 'custom', selected: true },
      { text: 'Daily', value: 'daily' },
      { text: 'Weekly', value: 'weekly' },
      { text: 'Biweekly', value: 'biweekly' },
      { text: 'Monthly', value: 'monthly' },
    ];
  }

  refreshFrequencyLabels(): void {
    const range = this.currentRange();
    if (!range.startTime) return;

    const tz = this.timezoneCode() || this.resolvedTimezone();
    const dow = this.timeService.getDayOfWeek(range.startTime, tz);
    const dayText = config.dayOfWeek[dow]?.name ?? '';

    const items = [...this.frequencyItems.filter(x => x.value !== 'last')];
    const weekly = items.find(x => x.value === 'weekly');
    if (weekly) weekly.text = `Weekly on ${dayText}`;

    const biWeekly = items.find(x => x.value === 'biweekly');
    if (biWeekly)
      biWeekly.text = `Every Other ${dayText} (starts from ${this.timeService.getDayAndMonth(range.startTime, tz)})`;

    const monthly = items.find(x => x.value === 'monthly');
    if (monthly) {
      const no = this.timeService.getWeekDayNo(range.startTime, tz);
      if (!no.isLast) {
        monthly.text = `Monthly on the ${no.text} ${dayText}`;
        monthly.value = 'monthly';
      } else if (no.isLast && no.text === 'last') {
        monthly.text = `Monthly on the last ${dayText}`;
        monthly.value = 'last';
      } else if (no.isLast && no.text !== 'last') {
        monthly.text = `Monthly on the ${no.text} ${dayText}`;
        monthly.value = 'monthly';
        items.push({ value: 'last', text: `Monthly on the last ${dayText}` });
      }
    }
    this.frequencyItems = items;
  }

}
